import { AfterViewInit, Component, DestroyRef, effect, ElementRef, inject, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { ContractService } from '../../core/services/contract.service';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { PdfService } from '../../core/services/pdf.service';
import { ContractRestService } from '../../core/api/contract-rest.service';
import { Template } from '../../core/models/template.model';
import { from } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

registerLocaleData(localeDe);

@Component({
  selector: 'app-checkout',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatTableModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    CurrencyPipe,
    MatNativeDateModule,
    MatDatepickerModule],
  templateUrl: './checkout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './checkout.component.scss',
})
export class CheckoutComponent implements AfterViewInit {

  account: any = {};
  validFrom: any = '';
  contracts: Template[] = [];
  loadingContracts = false;
  contractLoadError = '';
  exportingPdf = false;
  pdfExportError = '';
  contractService: ContractService = inject(ContractService);
  private contractRestService = inject(ContractRestService);
  pdfService: PdfService = inject(PdfService);
  private destroyRef = inject(DestroyRef);
  cart: any[] = [];
  private loadGeneration = 0;
  private checkoutSnapshot = '';

  displayedColumns: string[] = ['position', 'sku', 'name', 'once', 'monthly'];
  ongoingTouches = new Map<number, any>();
  @ViewChild('sign') sign!: ElementRef<HTMLCanvasElement>;
  renderer: Renderer2 = inject(Renderer2);
  ctx: any = null;
  offsetX = 0;
  offsetY = 0;

  constructor() {
    effect(() => {
      this.contractService.cartChanged();
      this.loadData();
    });

  }

  loadData() {
    const generation = ++this.loadGeneration;
    const acc = sessionStorage.getItem('account');
    const sel = sessionStorage.getItem('selectedContracts');
    const snapshot = JSON.stringify([sel, acc]);
    if (this.checkoutSnapshot && snapshot !== this.checkoutSnapshot && this.ctx) {
      this.clearCanvas();
      this.ongoingTouches.clear();
    }
    this.checkoutSnapshot = snapshot;
    this.validFrom = sessionStorage.getItem('valid_from') ?? sessionStorage.getItem('validFrom') ?? '';
    this.pdfExportError = '';

    try {
      this.cart = sel ? JSON.parse(sel) : [];
      this.account = acc ? JSON.parse(acc) : {};
      if (!Array.isArray(this.cart) || !this.account || typeof this.account !== 'object') {
        throw new Error('Invalid checkout data.');
      }
      if (this.account.id != null && this.cart.some(item => item.accountId != null &&
        String(item.accountId) !== String(this.account.id))) {
        throw new Error('The selected contracts belong to different accounts.');
      }
      const start = this.cart[0]?.valid_from ?? this.cart[0]?.validFrom ?? this.validFrom;
      const date = start ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(start) ? `${start}T00:00:00` : start) : undefined;
      this.validFrom = date && !Number.isNaN(date.getTime()) ? date : '';
    } catch (error) {
      this.cart = [];
      this.account = {};
      this.contracts = [];
      this.loadingContracts = false;
      this.contractLoadError = 'Der gespeicherte Warenkorb oder die Kundendaten sind ungültig. Bitte laden Sie die Seite neu.';
      console.error('Could not read checkout data from session storage:', error);
      return;
    }

    this.contracts = [];
    this.contractLoadError = '';
    if (this.cart.length === 0) {
      this.loadingContracts = false;
      return;
    }

    this.loadingContracts = true;
    from(Promise.all(this.cart.map(async selection => {
      const template = await this.contractRestService.getTemplateById(selection.templateId);
      return {
        ...template,
        templateId: selection.templateId,
        valid_from: selection.valid_from ?? selection.validFrom,
        valid_until: selection.valid_until ?? selection.validUntil,
        pos: selection.pos,
        price: template.price ?? selection.price ?? 0,
        monthly: template.monthly ?? selection.monthly ?? 0
      };
    })))
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (templates: Template[]) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.contracts = templates;
        this.loadingContracts = false;
      },
      error: (error: unknown) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.contracts = [];
        this.loadingContracts = false;
        this.contractLoadError = 'Die Vertragsvorlagen konnten nicht geladen werden. Bitte versuchen Sie es erneut.';
        console.error('Could not load checkout contract templates:', error);
      }
    });
  }

  ngAfterViewInit() {
    this.ctx = this.sign.nativeElement.getContext("2d");

    this.offsetX = this.sign.nativeElement.getBoundingClientRect().left;
    this.offsetY = this.sign.nativeElement.getBoundingClientRect().top;

    console.log('offset x' + this.offsetX);
    console.log('offset y' + this.offsetY);

    const stopStart = this.renderer.listen(this.sign.nativeElement, 'pointerdown', (event: any) => {
      this.handleStart(event);
    });
    const stopEnd = this.renderer.listen(this.sign.nativeElement, 'pointerup', (event: any) => {
      this.handleEnd(event);
    });
    const stopMove = this.renderer.listen(this.sign.nativeElement, 'pointermove', (event: any) => {
      this.handleMove(event);
    });
    const stopCancel = this.renderer.listen(this.sign.nativeElement, 'pointercancel', (event: any) => {
      this.handleCancel(event);
    });
    this.destroyRef.onDestroy(() => {
      [stopStart, stopEnd, stopMove, stopCancel].forEach(stop => stop());
    });

  }

  totalOnce() {
    return this.contracts.reduce((total, item) => total + (Number(item.price) || 0), 0);
  }

  totalMonthly() {
    return this.contracts.reduce((total, item) => total + (Number(item.monthly) || 0), 0);
  }

  handleStart(event: any) {
    const touch = {
      pageX: event.clientX,
      pageY: event.clientY,
      color: 'black',
    };
    this.ongoingTouches.set(event.pointerId, touch);


    this.offsetX = this.sign.nativeElement.getBoundingClientRect().left;
    this.offsetY = this.sign.nativeElement.getBoundingClientRect().top;

    this.ctx.beginPath();
    this.ctx.arc(touch.pageX-this.offsetX, touch.pageY-this.offsetY, 2, 0, Math.PI, false);
    this.ctx.fillStyle = touch.color;
    this.ctx.strokeStyle = touch.color;
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.stroke();

    event.preventDefault();
  }

  handleEnd(event: any) {
    const touch = this.ongoingTouches?.get(event.pointerId);

    if (!touch) {
      console.error(`End: Could not find touch ${event.pointerId}`);
      return;
    }

    console.log("end");
    console.log(touch);
    this.ctx.lineWidth = 2;
    this.ctx.fillStyle = touch.color;
    this.ctx.strokeStyle = touch.color;
    this.ctx.beginPath();
    this.ctx.moveTo(touch.pageX-this.offsetX, touch.pageY-this.offsetY);
    this.ctx.lineTo(event.clientX -this.offsetX, event.clientY-this.offsetY);
    // this.ctx.fillRect(event.x - 4, event.y - 4, 8, 8);
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.stroke();
    this.ongoingTouches.delete(event.pointerId);
  }

  handleMove(event: any) {
    const touch = this.ongoingTouches?.get(event.pointerId);

    // Event was not started
    if (!touch) {
      console.error(`Move: Could not find touch ${event.pointerId}`);
      return;
    }

    console.log("move");
    this.ctx.beginPath();
    this.ctx.moveTo(touch.pageX - this.offsetX, touch.pageY - this.offsetY);
    this.ctx.lineTo(event.clientX - this.offsetX, event.clientY - this.offsetY);
    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = "black";
    this.ctx.fillStyle = "black";
    this.ctx.lineJoin = "round";
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.stroke();

    const newTouch = {
      pageX: event.clientX,
      pageY: event.clientY,
      color: "black",
    };

    this.ongoingTouches?.set(event.pointerId, newTouch);
  }

  handleCancel(event: any) {
    const touch = this.ongoingTouches?.get(event.pointerId);
    console.log("cancel");
    if (!touch) {
      console.error(`Cancel: Could not find touch ${event.pointerId}`);
      return;
    }

    this.ongoingTouches?.delete(event.pointerId);
  }

  clearCanvas() {
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
  }

  async createDocument() {
    const current = JSON.stringify([sessionStorage.getItem('selectedContracts'), sessionStorage.getItem('account')]);
    if (current !== this.checkoutSnapshot) {
      this.loadData();
      return;
    }
    if (this.exportingPdf || this.loadingContracts || this.contractLoadError || this.contracts.length === 0) {
      return;
    }
    this.exportingPdf = true;
    this.pdfExportError = '';
    try {
      const signature = this.sign.nativeElement.toDataURL('image/png');
      await this.pdfService.createPdf(this.contracts, signature, this.account);
    } catch (error) {
      console.error('Could not create the checkout PDF:', error);
      this.pdfExportError = 'Der Vertrag konnte nicht als PDF erstellt werden. Bitte versuchen Sie es erneut.';
    } finally {
      this.exportingPdf = false;
    }
  }
}
